import { WorkflowObjectType } from '../../models/workflow.model';

/**
 * Catálogo de tokens `{{...}}` disponibles para interpolar en los campos de texto de la
 * config de un workflow (cuerpos de correo, mensajes, notas, payloads de webhook, títulos…).
 *
 * Equivalente workflow del `variables()` de chatflow. En chatflow el backend LISTA las
 * variables (`GET /contracts/variables`); en workflow NO existe ese endpoint: el backend
 * solo sabe RENDERIZAR tokens de un enrollment concreto (`WorkflowTokenRenderService` +
 * `POST /workflow-actions/render-tokens`), no enumerar los disponibles. Ver el bloque
 * GAP DE API del handoff.
 *
 * Mientras tanto, el catálogo se DERIVA de lo que ya sabemos en el frontend:
 * - El `objectType` del workflow → qué objeto base se carga y qué campos expone (la forma
 *   la fija `WorkflowTokenRenderService.mapContact/mapCompany/mapDeal/mapQuote`).
 * - El contexto estable del enrollment (`workflow.*`, `enrollment.*`, `object.*`).
 * - La salida del paso anterior (`latestStep.*`), lo único que el motor expone de forma
 *   estable como STEP_OUTPUT (el resto de salidas depende del subtipo y no es enumerable).
 *
 * Los tokens viajan SIN llaves; el picker las añade al insertar.
 */
export type WorkflowVariableSource = 'OBJECT' | 'ENROLLMENT' | 'STEP_OUTPUT';

/**
 * Qué FORMA tiene el valor con el que se compara una propiedad, para que el editor de valor deje
 * de ser siempre un `<input type="text">` (INC_CRM_020 (9) inc49/50/52/53/55).
 *
 * - `TEXT`     — texto libre (nombre, correo, asunto…).
 * - `NUMBER`   — número (`<input type="number">`).
 * - `DATE`     — fecha (`<input type="date">`).
 * - `BOOLEAN`  — Sí / No.
 * - `CATALOG`  — lista cerrada del negocio (estados de ticket, etapas del contacto…) → desplegable.
 * - `ENTITY`   — referencia a otro registro (empresa, contacto, responsable) → buscador por nombre.
 */
export type WorkflowPropertyValueKind = 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'CATALOG' | 'ENTITY';

/** Catálogo del negocio que sirve los valores de una propiedad `CATALOG`. */
export type WorkflowValueCatalogKey =
  | 'TICKET_STATUS'
  | 'TICKET_PRIORITY'
  | 'TICKET_CATEGORY'
  | 'TICKET_PIPELINE'
  | 'TICKET_STAGE'
  | 'CONTACT_CYCLE'
  | 'CONTACT_STATE'
  | 'CONTACT_ORIGIN'
  | 'DEAL_STAGE'
  | 'DEAL_ORIGIN'
  | 'DEAL_PRIORITY'
  | 'DEAL_BUSINESS_TYPE'
  | 'QUOTE_STATUS'
  | 'COMPANY_SECTOR'
  | 'COMPANY_SIZE'
  | 'COMPANY_CYCLE'
  | 'COMPANY_STATE'
  | 'COMPANY_RELATIONSHIP_TYPE'
  | 'COMPANY_TAX_TYPE'
  | 'CURRENCY'
  | 'TICKET_SOURCE'
  | 'TICKET_SOURCE_LABEL';

/** Entidad que sirve los valores de una propiedad `ENTITY` (se elige con un buscador por nombre). */
export type WorkflowValueEntityKey = 'USER' | 'CONTACT' | 'COMPANY' | 'DEAL';

/** Cómo se compara una propiedad: tipo del valor y de dónde salen sus opciones. */
export interface WorkflowPropertyValueDescriptor {
  kind?: WorkflowPropertyValueKind;
  catalog?: WorkflowValueCatalogKey;
  entity?: WorkflowValueEntityKey;
  /**
   * El valor guardado es el NOMBRE del elemento del catálogo, no su id. Es lo que corresponde a
   * las propiedades `*Name` que expone el backend (`ticket.statusName` trae "Abierto", no 3).
   */
  byName?: boolean;
}

export interface WorkflowVariable extends WorkflowPropertyValueDescriptor {
  /** Ruta del token sin llaves, p. ej. `contact.email`. */
  token: string;
  label: string;
  source: WorkflowVariableSource;
}

/** Campos del objeto base por `objectType`, calcados de `WorkflowTokenRenderService`. */
const OBJECT_FIELDS: Partial<Record<WorkflowObjectType, WorkflowVariable[]>> = {
  CONTACT: [
    { token: 'contact.id', label: 'ID del contacto', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'contact.firstname', label: 'Nombre', source: 'OBJECT' },
    { token: 'contact.fullname', label: 'Nombre completo', source: 'OBJECT' },
    { token: 'contact.lastname', label: 'Apellido', source: 'OBJECT' },
    { token: 'contact.email', label: 'Correo', source: 'OBJECT' },
    { token: 'contact.phone', label: 'Teléfono', source: 'OBJECT' },
    { token: 'contact.score', label: 'Puntaje', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'contact.ownerUserId', label: 'Responsable', source: 'OBJECT', kind: 'ENTITY', entity: 'USER' },
    // Ciclo y etapa: el renderizador del backend emite el id SIEMPRE y el nombre solo si el
    // catálogo resuelve ese id (si es huérfano, el token se omite igual que cualquier propiedad
    // ausente). Los `*Name` son de solo lectura → tokens sí, propiedad filtrable no.
    { token: 'contact.idContactCycle', label: 'Ciclo de vida', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_CYCLE' },
    { token: 'contact.contactCycleName', label: 'Ciclo de vida (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_CYCLE', byName: true },
    { token: 'contact.idContactState', label: 'Etapa', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_STATE' },
    { token: 'contact.contactStateName', label: 'Etapa (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_STATE', byName: true },
    { token: 'contact.idOrigin', label: 'Origen', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_ORIGIN' },
    { token: 'contact.position', label: 'Cargo', source: 'OBJECT' },
    { token: 'contact.isActive', label: 'Activo', source: 'OBJECT', kind: 'BOOLEAN' },
    // Única fecha del contacto. Sin ella, «Fecha programada» y «Esperar hasta fecha» no tenían
    // nada que ofrecer en un workflow de contacto.
    { token: 'contact.createdAt', label: 'Fecha de creación', source: 'OBJECT', kind: 'DATE' }
  ],
  COMPANY: [
    { token: 'company.id', label: 'ID de la empresa', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'company.name', label: 'Nombre', source: 'OBJECT' },
    { token: 'company.commercialName', label: 'Nombre comercial', source: 'OBJECT' },
    { token: 'company.businessName', label: 'Razón social', source: 'OBJECT' },
    { token: 'company.phone', label: 'Teléfono', source: 'OBJECT' },
    { token: 'company.website', label: 'Sitio web', source: 'OBJECT' },
    { token: 'company.ownerUserId', label: 'Responsable', source: 'OBJECT', kind: 'ENTITY', entity: 'USER' },
    { token: 'company.idCompanySector', label: 'Sector', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_SECTOR' },
    { token: 'company.idCompanySize', label: 'Tamaño', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_SIZE' },
    { token: 'company.idCompanyCycle', label: 'Ciclo de vida', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_CYCLE' },
    { token: 'company.idCompanyStateLead', label: 'Etapa', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_STATE' },
    { token: 'company.idCompanyRelationshipType', label: 'Tipo de relación', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_RELATIONSHIP_TYPE' },
    { token: 'company.idCompanyTaxType', label: 'Tipo de impuesto', source: 'OBJECT', kind: 'CATALOG', catalog: 'COMPANY_TAX_TYPE' },
    { token: 'company.contactId', label: 'Contacto principal', source: 'OBJECT', kind: 'ENTITY', entity: 'CONTACT' },
    { token: 'company.companyParentId', label: 'Empresa matriz', source: 'OBJECT', kind: 'ENTITY', entity: 'COMPANY' },
    { token: 'company.taxCode', label: 'Documento fiscal', source: 'OBJECT' },
    { token: 'company.createdAt', label: 'Fecha de creación', source: 'OBJECT', kind: 'DATE' },
    { token: 'contact.fullname', label: 'Contacto — nombre completo', source: 'OBJECT' },
    { token: 'contact.email', label: 'Contacto — correo', source: 'OBJECT' },
    { token: 'contact.phone', label: 'Contacto — teléfono', source: 'OBJECT' }
  ],
  DEAL: [
    { token: 'deal.id', label: 'ID del negocio', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'deal.name', label: 'Nombre', source: 'OBJECT' },
    { token: 'deal.amount', label: 'Monto', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'deal.companyId', label: 'Empresa', source: 'OBJECT', kind: 'ENTITY', entity: 'COMPANY' },
    { token: 'deal.ownerUserId', label: 'Responsable', source: 'OBJECT', kind: 'ENTITY', entity: 'USER' },
    // La etapa es EL filtro del módulo de negocios y no se podía elegir: `mapDeal` no la emitía.
    { token: 'deal.idDealCycle', label: 'Etapa', source: 'OBJECT', kind: 'CATALOG', catalog: 'DEAL_STAGE' },
    { token: 'deal.idDealOrigin', label: 'Origen', source: 'OBJECT', kind: 'CATALOG', catalog: 'DEAL_ORIGIN' },
    { token: 'deal.idDealPriority', label: 'Prioridad', source: 'OBJECT', kind: 'CATALOG', catalog: 'DEAL_PRIORITY' },
    { token: 'deal.idDealBusinessType', label: 'Tipo de negocio', source: 'OBJECT', kind: 'CATALOG', catalog: 'DEAL_BUSINESS_TYPE' },
    { token: 'deal.idLead', label: 'Lead de origen', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'deal.description', label: 'Descripción', source: 'OBJECT' },
    { token: 'deal.amountPerMonth', label: 'Monto mensual', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'deal.amountPerYear', label: 'Monto anual', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'deal.currencyId', label: 'Moneda', source: 'OBJECT', kind: 'CATALOG', catalog: 'CURRENCY' },
    { token: 'deal.archived', label: 'Archivado', source: 'OBJECT', kind: 'BOOLEAN' },
    { token: 'deal.estimatedCloseDate', label: 'Fecha estimada de cierre', source: 'OBJECT', kind: 'DATE' },
    { token: 'deal.closedAt', label: 'Fecha de cierre', source: 'OBJECT', kind: 'DATE' },
    { token: 'deal.createdAt', label: 'Fecha de creación', source: 'OBJECT', kind: 'DATE' },
    { token: 'contact.fullname', label: 'Contacto — nombre completo', source: 'OBJECT' },
    { token: 'contact.email', label: 'Contacto — correo', source: 'OBJECT' },
    { token: 'contact.phone', label: 'Contacto — teléfono', source: 'OBJECT' }
  ],
  TICKET: [
    { token: 'ticket.id', label: 'ID del ticket', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'ticket.ticketNumber', label: 'Número (TKT-…)', source: 'OBJECT' },
    { token: 'ticket.subject', label: 'Asunto', source: 'OBJECT' },
    { token: 'ticket.description', label: 'Descripción', source: 'OBJECT' },
    { token: 'ticket.statusName', label: 'Estado (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_STATUS', byName: true },
    { token: 'ticket.priorityName', label: 'Prioridad (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_PRIORITY', byName: true },
    { token: 'ticket.categoryName', label: 'Categoría (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_CATEGORY', byName: true },
    { token: 'ticket.pipelineName', label: 'Pipeline (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_PIPELINE', byName: true },
    // INC_CRM_020 (14) — estas cinco eran las únicas propiedades del ticket que seguían pidiendo
    // TEXTO PLANO: son las que se comparan por NOMBRE, justo las que el usuario no se sabe de
    // memoria ("¿la empresa es 'ACME' o 'Acme S.A.'?"). Con `byName` el editor ofrece el mismo
    // buscador/desplegable que sus gemelas por id, pero guarda el NOMBRE, que es lo que trae el
    // snapshot del registro (`WorkflowTokenRenderService.mapTicket`).
    { token: 'ticket.stageName', label: 'Etapa (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_STAGE', byName: true },
    { token: 'ticket.contactName', label: 'Contacto (nombre)', source: 'OBJECT', kind: 'ENTITY', entity: 'CONTACT', byName: true },
    { token: 'ticket.companyName', label: 'Empresa (nombre)', source: 'OBJECT', kind: 'ENTITY', entity: 'COMPANY', byName: true },
    { token: 'ticket.ownerUserName', label: 'Responsable (nombre)', source: 'OBJECT', kind: 'ENTITY', entity: 'USER', byName: true },
    // `sourceLabel` guarda el origen YA traducido ("Correo electrónico"), no la clave: lleva su
    // propio catálogo para que las etiquetas coincidan al carácter con `origenLegible()`.
    { token: 'ticket.sourceLabel', label: 'Origen', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_SOURCE_LABEL', byName: true },
    { token: 'ticket.reopenedCount', label: 'Veces reabierto', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'ticket.ownerUserId', label: 'Responsable', source: 'OBJECT', kind: 'ENTITY', entity: 'USER' },
    { token: 'ticket.idTicketStatus', label: 'Estado', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_STATUS' },
    { token: 'ticket.idTicketPriority', label: 'Prioridad', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_PRIORITY' },
    // Categoría y pipeline por id: `mapTicket` los emite y son justo los que inc52 pedía poder
    // elegir de una lista en vez de teclear a ciegas.
    { token: 'ticket.idTicketCategory', label: 'Categoría', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_CATEGORY' },
    { token: 'ticket.idPipeline', label: 'Pipeline', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_PIPELINE' },
    { token: 'ticket.source', label: 'Origen (clave)', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_SOURCE', byName: true },
    { token: 'ticket.idStage', label: 'Etapa del pipeline', source: 'OBJECT', kind: 'CATALOG', catalog: 'TICKET_STAGE' },
    { token: 'ticket.contactId', label: 'Contacto', source: 'OBJECT', kind: 'ENTITY', entity: 'CONTACT' },
    { token: 'ticket.companyId', label: 'Empresa', source: 'OBJECT', kind: 'ENTITY', entity: 'COMPANY' },
    // `mapTicket` emite los tres: sin ellos en la lista, filtrar por el negocio del ticket o por
    // si está archivado obligaba a escribir la clave a mano (INC_CRM_020 (13) inc52/57).
    { token: 'ticket.dealId', label: 'Negocio', source: 'OBJECT', kind: 'ENTITY', entity: 'DEAL' },
    { token: 'ticket.isArchived', label: 'Archivado', source: 'OBJECT', kind: 'BOOLEAN' },
    { token: 'ticket.createdAt', label: 'Fecha de creación', source: 'OBJECT', kind: 'DATE' },
    { token: 'ticket.resolvedAt', label: 'Fecha de resolución', source: 'OBJECT', kind: 'DATE' },
    { token: 'ticket.closedAt', label: 'Fecha de cierre', source: 'OBJECT', kind: 'DATE' },
    { token: 'contact.fullname', label: 'Contacto — nombre completo', source: 'OBJECT' },
    { token: 'contact.email', label: 'Contacto — correo', source: 'OBJECT' },
    { token: 'contact.phone', label: 'Contacto — teléfono', source: 'OBJECT' }
  ],
  QUOTE: [
    { token: 'quote.id', label: 'ID de la cotización', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'quote.number', label: 'Número', source: 'OBJECT' },
    { token: 'quote.totalAmount', label: 'Total', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'quote.contactPrimaryId', label: 'Contacto principal', source: 'OBJECT', kind: 'ENTITY', entity: 'CONTACT' },
    { token: 'quote.idQuoteStatus', label: 'Estado', source: 'OBJECT', kind: 'CATALOG', catalog: 'QUOTE_STATUS' },
    { token: 'quote.idDeal', label: 'Negocio', source: 'OBJECT', kind: 'ENTITY', entity: 'DEAL' },
    { token: 'quote.subtotal', label: 'Subtotal', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'quote.discount', label: 'Descuento', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'quote.version', label: 'Versión', source: 'OBJECT', kind: 'NUMBER' },
    { token: 'quote.currencyId', label: 'Moneda', source: 'OBJECT', kind: 'CATALOG', catalog: 'CURRENCY' },
    { token: 'quote.requiresApproval', label: 'Requiere aprobación', source: 'OBJECT', kind: 'BOOLEAN' },
    { token: 'quote.validUntil', label: 'Válida hasta', source: 'OBJECT', kind: 'DATE' },
    { token: 'quote.createdAt', label: 'Fecha de creación', source: 'OBJECT', kind: 'DATE' },
    // mapQuote adjunta el contacto principal bajo `contact.*` (incluidos ciclo y etapa).
    { token: 'contact.fullname', label: 'Contacto — nombre completo', source: 'OBJECT' },
    { token: 'contact.email', label: 'Contacto — correo', source: 'OBJECT' },
    { token: 'contact.idContactCycle', label: 'Contacto — ciclo de vida', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_CYCLE' },
    { token: 'contact.contactCycleName', label: 'Contacto — ciclo de vida (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_CYCLE', byName: true },
    { token: 'contact.idContactState', label: 'Contacto — etapa', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_STATE' },
    { token: 'contact.contactStateName', label: 'Contacto — etapa (nombre)', source: 'OBJECT', kind: 'CATALOG', catalog: 'CONTACT_STATE', byName: true }
  ]
};

/**
 * Propiedades que EXISTEN en el registro pero que el renderizador de tokens del backend
 * (`WorkflowTokenRenderService.mapContact/...`) NO expone.
 *
 * Se ofrecen en los selectores de PROPIEDAD (filtros, PROPERTY_CHANGED, SET_PROPERTY_VALUE,
 * condiciones) porque el motor sí las lee/escribe por nombre, pero se dejan FUERA del catálogo
 * de tokens `{{...}}`: un token que el renderizador no conoce se sustituiría por vacío y el
 * usuario no tendría forma de saberlo.
 *
 * `idContactCycle`/`idContactState` vivían aquí porque el renderizador no los emitía. Ya SÍ los
 * emite (junto con `contactCycleName`/`contactStateName`), así que se movieron a
 * `OBJECT_FIELDS.CONTACT` y siguen apareciendo como propiedad vía `buildPropertyOptions`.
 */
const PROPERTY_ONLY_FIELDS: Partial<Record<WorkflowObjectType, WorkflowPropertyOption[]>> = {};

/**
 * Tokens del objeto base que NO son propiedades del registro: son NOMBRES resueltos contra un
 * catálogo, de solo lectura. Sirven para interpolar (`{{contact.contactCycleName}}`) pero el
 * motor no sabe filtrar ni escribir por ellos, así que se excluyen de `app-wf-property-select`.
 */
const TOKEN_ONLY_PROPERTY_KEYS = new Set<string>(['contactCycleName', 'contactStateName']);

/** Tokens de contexto del enrollment/workflow, siempre disponibles. */
const ENROLLMENT_FIELDS: WorkflowVariable[] = [
  { token: 'workflow.id', label: 'ID del workflow', source: 'ENROLLMENT' },
  { token: 'workflow.name', label: 'Nombre del workflow', source: 'ENROLLMENT' },
  { token: 'workflow.ownerUserId', label: 'Responsable del workflow (id)', source: 'ENROLLMENT' },
  { token: 'enrollment.id', label: 'ID de la inscripción', source: 'ENROLLMENT' },
  { token: 'enrollment.objectType', label: 'Tipo de objeto inscrito', source: 'ENROLLMENT' },
  { token: 'enrollment.objectId', label: 'ID del objeto inscrito', source: 'ENROLLMENT' },
  { token: 'object.id', label: 'ID del registro actual', source: 'ENROLLMENT' }
];

/** Salida del paso anterior — lo único enumerable de forma estable como STEP_OUTPUT. */
const STEP_OUTPUT_FIELDS: WorkflowVariable[] = [
  { token: 'latestStep.id', label: 'ID del paso anterior', source: 'STEP_OUTPUT' },
  { token: 'latestStep.stepType', label: 'Tipo del paso anterior', source: 'STEP_OUTPUT' },
  { token: 'latestStep.stepSubtype', label: 'Subtipo del paso anterior', source: 'STEP_OUTPUT' },
  { token: 'latestStep.outputJson', label: 'Salida completa del paso anterior', source: 'STEP_OUTPUT' }
];

/**
 * Construye la lista plana de tokens disponibles para el workflow. El picker la agrupa por
 * `source`. Si el `objectType` no tiene un mapeo de objeto base (TICKET, CUSTOM_OBJECT,
 * SUBSCRIPTION), solo se ofrecen los tokens de contexto y de salida de paso.
 */
export function buildWorkflowVariableCatalog(objectType: WorkflowObjectType | null): WorkflowVariable[] {
  const objectFields = objectType ? (OBJECT_FIELDS[objectType] ?? []) : [];
  return [...objectFields, ...ENROLLMENT_FIELDS, ...STEP_OUTPUT_FIELDS];
}

/**
 * Una propiedad del objeto base: su clave (lo que se guarda), una etiqueta legible y —desde
 * INC_CRM_020 (9)— la FORMA de su valor, para que el editor de comparación se adapte
 * (desplegable del catálogo, buscador de registro, número o fecha) en vez de pedir texto plano.
 */
export interface WorkflowPropertyOption extends WorkflowPropertyValueDescriptor {
  key: string;
  label: string;
}

/** Prefijo del objeto base por `objectType`, para derivar la clave de propiedad sin él. */
const OBJECT_PREFIX: Partial<Record<WorkflowObjectType, string>> = {
  CONTACT: 'contact',
  COMPANY: 'company',
  DEAL: 'deal',
  QUOTE: 'quote',
  TICKET: 'ticket'
};

/**
 * Propiedades sugeridas del objeto base del workflow para los campos "propiedad" de la config
 * (p. ej. el disparador PROPERTY_CHANGED). Devuelve la CLAVE de la propiedad SIN el prefijo del
 * objeto (`contact.email` → `email`) + su etiqueta legible ("Correo").
 *
 * Es un SUBCONJUNTO conocido (campos estándar del objeto): los atributos personalizados NO
 * aparecen aquí — mismo GAP DE API que los tokens, no hay endpoint que liste propiedades. Por eso
 * el selector que la consume SIEMPRE deja escribir una propiedad libre ("Otra propiedad…"). Si el
 * `objectType` no tiene objeto base mapeado (TICKET, CUSTOM_OBJECT…), devuelve [] y el selector
 * cae a un input de texto libre.
 */
export function buildPropertyOptions(objectType: WorkflowObjectType | null): WorkflowPropertyOption[] {
  const prefix = objectType ? OBJECT_PREFIX[objectType] : undefined;
  if (!prefix) {
    return [];
  }
  const fromTokens = buildWorkflowVariableCatalog(objectType)
    .filter((v) => v.source === 'OBJECT' && v.token.startsWith(prefix + '.'))
    .map((v) => ({
      key: v.token.slice(prefix.length + 1),
      label: v.label,
      kind: v.kind,
      catalog: v.catalog,
      entity: v.entity,
      byName: v.byName
    }))
    .filter((o) => !TOKEN_ONLY_PROPERTY_KEYS.has(o.key));
  const extra = objectType ? (PROPERTY_ONLY_FIELDS[objectType] ?? []) : [];
  return [...fromTokens, ...extra.filter((e) => !fromTokens.some((o) => o.key === e.key))];
}

/**
 * Solo las propiedades de FECHA del objeto base.
 *
 * Los campos que piden una «propiedad de fecha» —el disparador «Fecha programada» y el nodo de
 * espera «Esperar hasta fecha»— ofrecían la lista COMPLETA: en un workflow de Negocio se elegía
 * entre 20 propiedades de las que solo 3 son fechas, y elegir «Monto» o «Etapa» se guardaba sin un
 * solo aviso. En ejecución el valor no se puede parsear como fecha, así que el flujo no se
 * programa nunca y el motivo solo queda escrito en la Cola de Eventos, donde nadie lo mira.
 *
 * Puede devolver [] legítimamente para un `objectType` sin objeto base mapeado. En ese caso el
 * selector cae a texto libre (para un atributo personalizado) y quien lo usa debe avisar de que no
 * hay ninguna propiedad de fecha para ese tipo de objeto.
 */
export function buildDatePropertyOptions(objectType: WorkflowObjectType | null): WorkflowPropertyOption[] {
  return buildPropertyOptions(objectType).filter((option) => option.kind === 'DATE');
}

/**
 * Descriptor de valor de una propiedad DENTRO de una lista de opciones.
 *
 * La clave se compara sin el prefijo del objeto y sin distinguir mayúsculas, para que valga
 * igual con lo que guardó el selector (`idTicketStatus`) que con lo que pudiera haber escrito
 * alguien a mano (`ticket.idTicketStatus`). Una propiedad libre (atributo personalizado, campo
 * del evento) devuelve `undefined` → el editor cae al texto de siempre.
 */
export function findPropertyDescriptor(
  options: readonly WorkflowPropertyOption[],
  property: string | null | undefined
): WorkflowPropertyOption | undefined {
  const raw = (property ?? '').trim();
  if (!raw) {
    return undefined;
  }
  const key = raw.includes('.') ? raw.slice(raw.lastIndexOf('.') + 1) : raw;
  const needle = key.toLowerCase();
  return options.find((o) => o.key.toLowerCase() === needle);
}
